import React from 'react';
import { useTranslation } from 'react-i18next';
import { Listing } from '../../types';
import { pickLang, telLink } from '../../utils/listingDisplay';

interface Row {
  label: string;
  value: React.ReactNode;
}

/**
 * Category-specific details (plan section 6), under a heading. Only fields that
 * are filled in are shown; nothing at all (not even the heading) when none are.
 */
export default function ListingDetailsBlock({ listing, title }: { listing: Listing; title: string }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const f = (key: string) => t(`directory.field.${key}`);
  const text = (en?: string, he?: string) => pickLang(en, he, isHebrew) || undefined;

  const rows: (Row | false | undefined)[] = [];
  /** Yes/no fields that are switched on, shown as tags. */
  const flags: string[] = [];
  const flag = (on: boolean | undefined, labelKey: string) => {
    if (on) flags.push(f(labelKey));
  };
  const row = (labelKey: string, value: React.ReactNode | undefined) =>
    value !== undefined && value !== '' && value !== null ? { label: f(labelKey), value } : undefined;

  switch (listing.category) {
    case 'gemach': {
      const d = listing.details;
      rows.push(
        row('itemsDescription', text(d.itemsDescriptionEn, d.itemsDescriptionHe)),
        row('lendingType', d.lendingType && t(`directory.lendingType.${d.lendingType}`)),
        row('feeNote', text(d.feeNoteEn, d.feeNoteHe))
      );
      flag(d.depositRequired, 'depositRequired');
      flag(d.pickupOnly, 'pickupOnly');
      flag(d.donationsAccepted, 'donationsAccepted');
      break;
    }
    case 'mikvah': {
      const d = listing.details;
      rows.push(
        row('mikvahType', d.mikvahType && t(`directory.sub.${d.mikvahType}`)),
        row('feeNote', text(d.feeNoteEn, d.feeNoteHe)),
        row('supervision', text(d.supervisionEn, d.supervisionHe))
      );
      flag(d.appointmentRequired, 'appointmentRequired');
      flag(d.accessible, 'accessible');
      break;
    }
    case 'beisHoraah': {
      const d = listing.details;
      rows.push(
        row(
          'rabbanim',
          d.rabbanim?.length ? (
            <ul className="space-y-0.5">
              {d.rabbanim.map((r, i) => {
                const role = text(r.roleEn, r.roleHe);
                return (
                  <li key={i}>
                    {text(r.nameEn, r.nameHe)}
                    {role && <span className="text-ash"> · {role}</span>}
                  </li>
                );
              })}
            </ul>
          ) : undefined
        ),
        row('languages', d.languages?.length ? d.languages.map((c) => t(`directory.language.${c}`)).join(', ') : undefined),
        row('topics', d.topics?.length ? d.topics.map((k) => t(`directory.topic.${k}`)).join(', ') : undefined),
        row(
          'shailosLinePhone',
          d.shailosLinePhone ? (
            <a href={telLink(d.shailosLinePhone)} className="underline" dir="ltr">
              {d.shailosLinePhone}
            </a>
          ) : undefined
        )
      );
      flag(d.walkInAllowed, 'walkInAllowed');
      break;
    }
    case 'organization': {
      const d = listing.details;
      rows.push(
        row('services', text(d.servicesEn, d.servicesHe)),
        row(
          'donationLink',
          d.donationLink ? (
            <a href={d.donationLink} target="_blank" rel="noopener noreferrer" className="underline break-all">
              {d.donationLink}
            </a>
          ) : undefined
        )
      );
      flag(d.emergency, 'emergency');
      flag(d.available247, 'available247');
      break;
    }
    case 'service': {
      const d = listing.details;
      rows.push(
        row('capacity', d.capacity !== undefined ? String(d.capacity) : undefined),
        row('beds', d.beds !== undefined ? String(d.beds) : undefined),
        row('priceNote', text(d.priceNoteEn, d.priceNoteHe)),
        row('statusMessage', text(d.statusMessageEn, d.statusMessageHe))
      );
      if (d.free !== undefined) flags.push(t(d.free ? 'directory.filter.free' : 'directory.filter.rent'));
      break;
    }
    case 'shiur': {
      const d = listing.details;
      const languages = (d?.languages ?? []).map((c) => t(`directory.language.${c}`)).join(', ');
      // Rows use the shiur labels; flags list who it's for.
      rows.push(
        text(d?.giverEn, d?.giverHe) ? { label: t('directory.shiur.giver'), value: text(d?.giverEn, d?.giverHe) } : undefined,
        languages ? { label: t('directory.shiur.languages'), value: languages } : undefined
      );
      for (const a of d?.audience ?? []) flags.push(t(`directory.audience.${a}`));
      break;
    }
    default:
      break;
  }

  const shown = rows.filter((r): r is Row => !!r);
  if (shown.length === 0 && flags.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {shown.length > 0 && (
        <dl className="space-y-2 text-sm">
          {shown.map((r) => (
            <div key={r.label}>
              <dt className="text-ash text-xs">{r.label}</dt>
              <dd className="text-ink">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((label) => (
            <span key={label} className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium">
              {label}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
